import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import Feed from "./pages/Feed";
import Promotions from "./pages/Promotions";
import Analytics from "./pages/Analytics";
import AITools from "./pages/AITools";
import AdminPanel from "./pages/AdminPanel";
import Leaderboard from "./pages/Leaderboard";
import LandingPages from "./pages/LandingPages";
import Leads from "./pages/Leads";
import PublicLandingPage from "./pages/PublicLandingPage";
import Billing from "./pages/Billing";
import VideoAds from "./pages/VideoAds";
import ScheduledMessages from "./pages/ScheduledMessages";
import Support from "./pages/Support";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import VerifyEmail from "./pages/VerifyEmail";
import EmailVerificationPending from "./pages/EmailVerificationPending";

// Layout
import DashboardLayout from "./components/DashboardLayout";

const ProtectedRoute = ({ children }) => {
  const { user, loading, isEmailVerified } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  // Check email verification
  if (!isEmailVerified()) {
    return <Navigate to="/verify-pending" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isEmailVerified } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (!isEmailVerified()) {
    return <Navigate to="/verify-pending" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          } />
          <Route path="/lp/:slug" element={<PublicLandingPage />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-pending" element={<EmailVerificationPending />} />
          
          {/* Protected Dashboard Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="feed" element={<Feed />} />
            <Route path="promotions" element={<Promotions />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="ai-tools" element={<AITools />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="landing-pages" element={<LandingPages />} />
            <Route path="leads" element={<Leads />} />
            <Route path="video-ads" element={<VideoAds />} />
            <Route path="scheduled-messages" element={<ScheduledMessages />} />
            <Route path="billing" element={<Billing />} />
          </Route>
          
          {/* Admin Route */}
          <Route path="/admin" element={
            <AdminRoute>
              <DashboardLayout isAdmin />
            </AdminRoute>
          }>
            <Route index element={<AdminPanel />} />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
